/** USD-only formatter, used for transaction cost-basis display (always stored in USD) */
export function formatCurrency(value: number, compact = false): string {
  if (compact && Math.abs(value) >= 1_000_000) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 2,
    }).format(value);
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: value < 1 ? 6 : 2,
  }).format(value);
}

/**
 * AUD formatter. The Tax Harvest Estimator (and therefore every saved Tax Plan
 * estimate) is denominated in AUD, so Tax Plan figures are formatted here rather
 * than through the currency-context `fmt` (which converts from USD).
 */
export function formatAUD(value: number, opts?: { sign?: boolean }): string {
  const d = Math.abs(value) < 1 && value !== 0 ? 6 : 2;
  // en-US (not en-AU) so AUD renders as "A$" — matching the app-wide convention
  // (CurrencyContext.fmt / the planners' fmtTotal). en-AU would render a bare "$".
  const str = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  }).format(Math.abs(value));
  if (value < 0) return `−${str}`;
  if (opts?.sign && value > 0) return `+${str}`;
  return str;
}

/** Currency symbol for a code (e.g. "$", "€", "¥"). Falls back to "CODE". */
export function currencySymbol(code: string): string {
  try {
    const sym = (0)
      .toLocaleString('en-US', { style: 'currency', currency: code, minimumFractionDigits: 0, maximumFractionDigits: 0 })
      .replace(/[\d\s]/g, '')
      .trim();
    return sym || code;
  } catch {
    return code;
  }
}

/**
 * Decimal places a typed value actually carries, with floating-point artifacts
 * removed (so 1.11 never renders as "1.1100000000001"). Capped at 8.
 */
function exactDecimals(abs: number): number {
  if (!isFinite(abs) || abs === 0) return 0;
  const trimmed = abs.toFixed(8).replace(/0+$/, '').replace(/\.$/, '');
  const dot = trimmed.indexOf('.');
  return dot === -1 ? 0 : trimmed.length - dot - 1;
}

/**
 * Formatter #1 — USER-ENTERED prices. Preserves the EXACT precision the user typed:
 * only a currency symbol and integer thousands separators are added. No decimal
 * places are padded or rounded away. 1.112 → "$1.112", 0.85 → "$0.85",
 * 1112.5 → "$1,112.5", 1 → "$1". `value` must already be in `currencyCode`.
 */
export function formatExactPrice(value: number, currencyCode: string): string {
  if (!isFinite(value)) return `${currencySymbol(currencyCode)}0`;
  const dec = exactDecimals(Math.abs(value));
  const body = Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });
  return `${value < 0 ? '-' : ''}${currencySymbol(currencyCode)}${body}`;
}

/** Magnitude-adaptive decimal count for fetched market / display prices. */
export function marketDecimals(abs: number): number {
  if (abs >= 1)      return 2;
  if (abs >= 0.01)   return 4;
  if (abs >= 0.0001) return 6;
  if (abs > 0)       return 8;
  return 2;
}

/**
 * Formatter #2 — MARKET / display prices (live prices, current price, Market tab,
 * coin headers). Decimals scale to the coin's magnitude so small coins aren't
 * rounded to whole dollars. `value` must already be in `currencyCode`.
 */
export function formatMarketPrice(value: number, currencyCode: string): string {
  const dec = marketDecimals(Math.abs(value));
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: dec,
      maximumFractionDigits: dec,
    }).format(value);
  } catch {
    return `${currencySymbol(currencyCode)}${value.toFixed(dec)}`;
  }
}

/**
 * Formatter #3 — aggregate money (totals, budgets, proceeds, portfolio values).
 * Already denominated in a specific currency (NOT USD), so it bypasses `fmt()`.
 * Clean 2-decimal formatting for the typical case.
 */
export function formatMoney(amount: number, currencyCode: string, decimals?: number): string {
  const d = decimals ?? (Math.abs(amount) < 1 && amount !== 0 ? 6 : 2);
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    }).format(amount);
  } catch {
    return `${currencyCode} ${amount.toFixed(d)}`;
  }
}

/**
 * Formatter #4 — compact chart-axis money ("A$40k", "$1.2M", "¥6M"). `value` must
 * already be in `currencyCode`. Same "A$"-style symbol as every other formatter
 * here (en-US locale), so an axis never disagrees with the figures beside it.
 * One decimal only where it carries information (A$2.5k, not A$40.0k).
 *
 * `precise` is for a readout of ONE value (a crosshair label) rather than a row
 * of axis ticks: one decimal on thousands and two on millions and billions
 * (A$33.2k, A$1.25M), trailing zeros still trimmed.
 */
export function formatCompactMoney(
  value: number,
  currencyCode: string,
  opts: { precise?: boolean } = {}
): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  const sym = currencySymbol(currencyCode);
  const scaled = (n: number, suffix: string, preciseDecimals: number) => {
    const d = opts.precise ? preciseDecimals : n < 10 ? 1 : 0;
    const body = n.toFixed(d).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
    return `${sign}${sym}${body}${suffix}`;
  };
  if (abs >= 1e9) return scaled(abs / 1e9, 'B', 2);
  if (abs >= 1e6) return scaled(abs / 1e6, 'M', 2);
  if (abs >= 1e3) return scaled(abs / 1e3, 'k', 1);
  return `${sign}${sym}${abs.toFixed(0)}`;
}

export function formatPercent(value: number, showSign = true): string {
  const sign = showSign && value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

export function formatAmount(value: number): string {
  if (value === 0) return '0';
  if (value < 0.00001) return value.toExponential(4);
  if (value < 1) return value.toFixed(6);
  if (value < 1000) return value.toFixed(4);
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 }).format(value);
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatDateShort(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}
